/**
 * © 2025 Grupo Salamander SAPI de CV
 *
 * Navigation component overrides for the Renaissance design system.
 * Covers MuiTabs, MuiTab, MuiStepLabel, MuiStepIcon, MuiIconButton.
 */

import type { TPaletteDefinition } from '../palette'
import { focusRing } from '../focusRing'
import {
  density,
  radii,
  radius,
  sizing,
  transitionFor,
  type TDensity,
} from '../tokens'

export function navigationOverrides(
  palette: TPaletteDefinition,
  mode: 'dark' | 'light',
  densityKey: TDensity = 'regular',
  reducedMotion = false
) {
  const dark = mode === 'dark'
  const d = density[densityKey]
  const ring = focusRing(palette, mode)

  return {
    MuiTabs: {
      styleOverrides: {
        root: {
          backgroundColor: palette.gray6.main,
        },
      },
    },
    MuiTab: {
      defaultProps: { disableRipple: true },
      styleOverrides: {
        root: {
          textTransform: 'none' as const,
          minHeight: d.controlHeight,
          borderRadius: radius.row,
          // Tab is also `styled(ButtonBase)` — `Mui-focusVisible`, same as
          // every other role here.
          '&.Mui-focusVisible': ring,
        },
      },
    },
    MuiStepLabel: {
      styleOverrides: {
        label: {
          fontSize: sizing.stepLabel,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          display: '-webkit-box',
          WebkitLineClamp: '1',
          WebkitBoxOrient: 'vertical' as const,
          '&.MuiStepLabel-alternativeLabel': {
            marginTop: 1,
          },
        },
      },
    },
    MuiStepIcon: {
      styleOverrides: {
        root: {
          fontSize: sizing.stepIcon,
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: radii.sm,
          transition: transitionFor(
            'feedback',
            ['background-color'],
            reducedMotion
          ),
          '&:hover': {
            backgroundColor: dark
              ? 'rgba(255,255,255,0.08)'
              : 'rgba(0,0,0,0.04)',
          },
          '&:active': {
            backgroundColor: dark
              ? 'rgba(255,255,255,0.16)'
              : 'rgba(0,0,0,0.1)',
          },
          '&.Mui-focusVisible': ring,
        },
      },
    },
  }
}
